import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'underscore';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import { IconButton, Tooltip, TextField, Select, MenuItem } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import TimePicker from 'components/DatePicker/TimePicker';
import EditIcon from '@mui/icons-material/EditOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import DataTable from '../../../../components/DataTable/DataTable.js';
import { EditTasksDetails } from '../../actions/BridgeCallActions.js';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	infoButton: {
		color: theme.palette.info.main
	},
	customTable: {
		'& .MuiTable-root': {
			borderCollapse: 'collapse',
		},
		'& .MuiTableCell-root': {
			border: '1px solid black',
			paddingTop: 0,
			paddingBottom: 0,
		},
	},
}));

const BridgeCallDatatable = (props) => {
	const { className, data, tableName, bridgeStatus, disabled, ...rest } = props;
	const dispatch = useDispatch();
	const classes = useStyles();
	const userId = useSelector(state => state.session.user.id);
	const dropdowns = useSelector((state) => state.bridgeCallReducer.dropdowns);

	const [celldata, setCelldata] = useState([]);

	useMemo(() => {
		if (Array.isArray(data)) {
			setCelldata(data);
		}
	}, [data]);

	const handleChange = (value, rowIndex, columnName) => {
		const updatedData = [...celldata];
		updatedData[rowIndex] = {
			...updatedData[rowIndex],
			[columnName]: value,
		};
		setCelldata(updatedData);

		updatedData[rowIndex] = {
			...updatedData[rowIndex],
			userId: userId,
		};

		dispatch(EditTasksDetails(updatedData[rowIndex]));
	};

	// const handleChangeDropdown = (value, rowIndex, columnName) => {
	// 	const updatedData = [...celldata];
	// 	updatedData[rowIndex] = {
	// 		...updatedData[rowIndex],
	// 		[columnName]: value,
	// 	};
	// 	setCelldata(updatedData);

	// 	console.log(updatedData);
	// };

	const excludedTasks = [
		"Operational Pre Checks",
		"S0 Scripts Implementation",
		"S1 Scripts Implementation",
		"S2 Scripts Implementation",
		"S3 Scripts Implementation",
		"S1+S2 Scripts Implementation",
		"S1+S3 Scripts Implementation",
		"S1+S2+S3 Scripts Implementation",
		"Operational Post Checks",
		"Initial Stats / No Warm",
		"CRQ/TASKs closed"
	];

	const EditableTextField = ({ value, onChange }) => {
		const [inputValue, setInputValue] = useState(value ?? '');

		useEffect(() => {
			setInputValue(value ?? '');
		}, [value]);

		const triggerChange = () => {
			onChange(inputValue);
		};

		return (
			<TextField
				value={inputValue}
				onChange={(e) => setInputValue(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						triggerChange();
					}
				}}
				disabled={disabled}
				onBlur={triggerChange}
				fullWidth
			/>
		);
	};

	const _getTableColumns = () => (
		[
			{
				name: 'id',
				label: 'Id',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'insertDate',
				label: 'InsertDate',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'insertUser',
				label: 'InsertUser',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'vendor',
				label: 'Vendor',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'task',
				label: 'Task',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Task - ${v}`
					},
					setCellProps: () => ({
						style: { minWidth: 300, width: 130 },
					}),
					customBodyRender: (value) => {
						const isExcluded = excludedTasks.includes(value);
						return (
							<span style={{ fontWeight: isExcluded ? 'bold' : 'normal' }}>
								{value}
							</span>
						);
					}
				}
			},
			{
				name: 'status',
				label: 'Status',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Status - ${v}`
					},
					setCellProps: () => ({
						style: { minWidth: 200, width: 130 },
					}),
					customBodyRender: (value, tableMeta) => {
						const rowIndex = tableMeta.rowIndex;
						const task = celldata?.[rowIndex]?.task;

						if (excludedTasks.includes(task)) {
							return '';
						}

						return (
							<Select
								value={dropdowns.some(d => d.value === value) ? value : ''}
								onChange={(e) =>
									handleChange(
										e.target.value,
										rowIndex,
										tableMeta.columnData.name
									)
								}
								variant="standard"
								fullWidth
								disabled={disabled}
							>
								{dropdowns.map((item) => (
									<MenuItem key={item.value} value={item.value}>
										{item.text}
									</MenuItem>
								))}
								{/* <MenuItem value="Active">Active</MenuItem>
								<MenuItem value="Pending">Pending</MenuItem>
								<MenuItem value="Completed">Completed</MenuItem>
								<MenuItem value="Cancelled">Cancelled</MenuItem> */}
							</Select>
						);
					}
				}
			},
			{
				name: 'startTime',
				label: 'Start Time',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Start Time - ${v}`
					},
					setCellProps: () => ({
						style: { minWidth: 120, maxWidth: 140, width: 130, padding: '4px' },
					}),
					customBodyRender: (value, tableMeta) => {
						const rowIndex = tableMeta.rowIndex;
						const task = celldata?.[rowIndex]?.task;

						if (excludedTasks.includes(task)) {
							return '';
						}

						const currentValue = celldata?.[rowIndex]?.startTime;
						const dateValue = currentValue ? moment(value, 'YYYY-MM-DD HH:mm:ss').add(1, 'hours') : null;

						return (
							<TimePicker
								value={currentValue}
								onAccept={(newValue) => {
									if (!newValue) return;
									const formatted = moment.parseZone(newValue).format('YYYY-MM-DDTHH:mm:ss');
									handleChange(formatted, rowIndex, 'startTime');
								}}
								onChange={(newValue) => {
									if (!newValue) return;
									const formatted = moment.parseZone(newValue).format('YYYY-MM-DDTHH:mm:ss');
									handleChange(formatted, rowIndex, 'startTime');
								}}
								disabled={disabled}
							/>
						);
					},
				},
			},
			{
				name: 'endTime',
				label: 'End Time',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `End Time - ${v}`
					},
					setCellProps: () => ({
						style: { minWidth: 120, maxWidth: 140, width: 130, padding: '4px' },
					}),
					customBodyRender: (value, tableMeta) => {
						const rowIndex = tableMeta.rowIndex;
						const task = celldata?.[rowIndex]?.task;

						if (excludedTasks.includes(task)) {
							return '';
						}

						const currentValue = celldata?.[rowIndex]?.endTime;
						const dateValue = currentValue ? moment(currentValue, 'YYYY-MM-DD HH:mm:ss').add(1, 'hours') : null;

						return (
							<TimePicker
								value={currentValue}
								onAccept={(newValue) => {
									if (!newValue) return;
									const formatted = moment.parseZone(newValue).format('YYYY-MM-DDTHH:mm:ss');
									handleChange(formatted, rowIndex, 'endTime');
								}}
								onChange={(newValue) => {
									if (!newValue) return;
									const formatted = moment.parseZone(newValue).format('YYYY-MM-DDTHH:mm:ss');
									handleChange(formatted, rowIndex, 'endTime');
								}}
								disabled={disabled}
							/>
						);
					},
				},
			},
			{
				name: 'duration',
				label: 'Duration',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Duration - ${v}`
					},
					setCellProps: () => ({
						style: { padding: 4, minWidth: 30, width: 10 },
					}),
					customBodyRender: (value, tableMeta) => {
						const rowIndex = tableMeta.rowIndex;
						const task = celldata?.[rowIndex]?.task;

						if (excludedTasks.includes(task)) {
							return '';
						}

						return (
							<TextField
								disabled={true}
								fullWidth
								value={value}
								// onChange={(newValue) =>
								// 	handleChange(newValue, tableMeta.rowIndex, tableMeta.columnData.name)
								// }
							/>
						);
					},
				},
			},
			{
				name: 'comments',
				label: 'Comments',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Comments - ${v}`
					},
					setCellProps: () => ({
						style: { padding: 4 },
					}),
					customBodyRender: (value, tableMeta) => {
						const rowIndex = tableMeta.rowIndex;
						const task = celldata?.[rowIndex]?.task;

						if (excludedTasks.includes(task)) {
							return '';
						}

						return (
							<EditableTextField
								value={value}
								onChange={(newValue) =>
									handleChange(newValue, tableMeta.rowIndex, tableMeta.columnData.name)
								}
								disabled={bridgeStatus === 'CLOSED'}
							/>
						);
					},
				},
			},
		]
	);

	const _getTableOptions = () => ({
		filterType: 'dropdown',
		selectableRows: 'none',
		pagination: false,
		search: false,
		filter: false,
		viewColumns: false,
		setTableProps: () => ({
			style: {
				borderCollapse: 'collapse',
			},
		}),
		setCellProps: () => ({
			style: {
				border: '1px solid black',
			},
		}),
	});

	const componentMapping = {
		Skeleton,
	};

	const renderComponentCell = (componentString) => {
		const Component = componentMapping[componentString];
		return Component ? <Component /> : null;
	};

	let columnsSchema = '';
	const getColumns = (el) => {
		columnsSchema = `${columnsSchema}"${el.name}": "Skeleton", `;
	};

	_getTableColumns().forEach(element => getColumns(element));

	columnsSchema = columnsSchema.slice(0, -2);
	const parsed = JSON.parse(`{${columnsSchema}}`);
	const novo = [];

	let i = 1;
	while (i <= 5) {
		novo.push(parsed);

		i++;
	}

	let updatedData = [];
	if (Array.isArray(data) && data.length > 0) {
		updatedData = novo.slice(0, 10).map((item) => {
			const updatedItem = {};

			Object.keys(item).forEach((key) => {
				updatedItem[key] = renderComponentCell('Skeleton');
			});

			return updatedItem;
		});
	}

	return (
		<div {...rest} className={classes.customTable}>
			<DataTable
				id="bridgeCallTable"
				title=""
				data={data === 'none' ? updatedData : celldata}
				columns={_getTableColumns()}
				tableBodyMaxHeight="40em"
				tableBodyCellMaxWidth="250"
				options={_getTableOptions()}
			/>
		</div>
	);
};

BridgeCallDatatable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
	tableName: PropTypes.string,
	bridgeStatus: PropTypes.string,
	disabled: PropTypes.bool,
};

export default BridgeCallDatatable;
